import type {
  ApPaymentStatus,
  ApPaymentType,
  BankAccountType,
  PaymentMethodType,
  PixKeyType,
} from '../generated/enums.js';
import type { ReconciliationAdjustmentResponse } from '../reconciliation/adjustments.js';
import type { PaymentDispatchProviderId } from './payment-dispatch-providers.js';

export * from './payment-dispatch-providers.js';

export type PaymentReconciliationAdjustmentResponse = Pick<
  ReconciliationAdjustmentResponse,
  'adjustmentType' | 'amount' | 'description' | 'groupId' | 'paymentId' | 'reconciliationLinkId'
> & {
  createdByUserName?: string;
};

export interface PreferredBankUnavailableWarning {
  code: 'PREFERRED_BANK_UNAVAILABLE';
  preferredOriginBankAccountId: string;
  usedOriginBankAccountId: string | null;
  reason: 'NOT_FOUND' | 'INACTIVE' | 'DELETED' | 'ENTITY_MISMATCH';
}

/**
 * The payment carries a dynamic Pix BR Code but its origin account's provider
 * cannot send one (TecnoSpeed form 47). The payment is saved; it will need a
 * different origin or a manual payment.
 */
export interface PixDynamicQrOriginUnsupportedWarning {
  code: 'PIX_DYNAMIC_QR_ORIGIN_UNSUPPORTED';
  originBankAccountId: string;
}

/**
 * The payment pays the payee's bank details by a rail its origin account sends no automatic
 * transfer for: PIX by bank details (`PIX_BANK_DATA_ORIGIN_UNSUPPORTED`, e.g. Banco do Brasil
 * on TecnoSpeed, or a key-only gateway) or TED (`TED_ORIGIN_UNSUPPORTED`). Saved; it needs
 * another origin or rail before it is sent.
 */
export interface TransferRailOriginUnsupportedWarning {
  code: 'PIX_BANK_DATA_ORIGIN_UNSUPPORTED' | 'TED_ORIGIN_UNSUPPORTED';
  originBankAccountId: string;
}

export type PaymentCreationWarning =
  | PreferredBankUnavailableWarning
  | PixDynamicQrOriginUnsupportedWarning
  | TransferRailOriginUnsupportedWarning;

/**
 * Where a PIX/TED payment sends money, decided from the payment content:
 * - `REGISTERED`: a payee bank account on file (TED/bank data), or a Pix key
 *   equal to a key registered on one of the payee's accounts.
 * - `AD_HOC`: a Pix key typed for this payment only.
 * - `COPIA_E_COLA`: a valid BCB BR Code.
 */
export type PaymentTransferDestination = 'REGISTERED' | 'AD_HOC' | 'COPIA_E_COLA';

/** Rails an origin bank account can send, as decided by the payment send path. */
export interface PaymentOriginCapabilitiesResponse {
  bankAccountId: string;
  /**
   * The origin sends payments automatically (provider, bank-file routes or CNAB bank).
   * The rails restrict automatic sends only; with `false` every rail is false and a
   * payment from this origin is paid outside the ERP and recorded (PIX or TED).
   */
  automaticSend: boolean;
  /** An ACH transfer in dollars can be sent from this origin (Brex Cash accounts). */
  ach: boolean;
  pixKey: boolean;
  pixDynamicQr: boolean;
  pixBankData: boolean;
  ted: boolean;
}

export interface PaymentResponse {
  tenantId: string;
  paymentId: string;
  scheduleId: string;
  billId: string;
  paymentNumber: number;
  paymentType: ApPaymentType;
  scheduledAmount: number;
  paidAmount: number | null;
  /** Functional currency of the AP obligation. */
  currencyCode?: string;
  /** Currency of the bank/card movement that settled this payment. */
  settlementCurrencyCode?: string | null;
  /** Foreign obligation units reserved for this payment. */
  foreignScheduledAmount?: number | null;
  /** Foreign obligation units relieved by this settlement. */
  foreignAmountRelieved?: number | null;
  /** Functional carrying amount relieved, distinct from actual cash. */
  functionalCarryingRelieved?: number | null;
  /** Actual functional cash/card amount, distinct from carrying. */
  functionalCashAmount?: number | null;
  settlementExchangeRate?: number | null;
  settlementRateSource?: string | null;
  settlementRateDate?: string | null;
  realizedFxAmount?: number | null;
  remainingForeignAmountAfter?: number | null;
  remainingFunctionalCarryingAfter?: number | null;
  scheduledDate: string;
  documentDueDate: string | null;
  paidDate: string | null;
  paymentMethod: PaymentMethodType | null;
  bankAccountId: string | null;
  /** Employee/party payee bank account, e.g. a payroll (RPA) beneficiary. */
  partyBankAccountId: string | null;
  originBankAccountId: string | null;
  creditCardId: string | null;
  /** CC purchase installment count (1–24); set only for CREDIT_CARD/DEBIT_CARD. */
  ccInstallmentCount: number | null;
  barcode: string | null;
  boletoNossoNumero: string | null;
  pixCopiaECola: string | null;
  /** DICT type of the Pix key in `pixCopiaECola`; null for BR Codes, non-PIX and legacy rows. */
  pixKeyType: PixKeyType | null;
  /**
   * PIX by the payee account's bank details (agência/conta, no key). False for PIX by key and
   * every other method. Always present in API responses.
   */
  pixByBankData?: boolean;
  /** PIX/TED destination kind; null for every other payment method. */
  transferDestination: PaymentTransferDestination | null;
  pixTxId: string | null;
  pixEndToEndId: string | null;
  tedDocNumber: string | null;
  withholdingTaxType: string | null;
  sourceDocumentId: string | null;
  status: ApPaymentStatus;
  isAutomaticBankPayment: boolean;
  // External payment provider tracking (Kamino, bank, etc.)
  externalPaymentId: string | null;
  externalPaymentProvider?: PaymentDispatchProviderId | null;
  externalPaymentSentAt: string | null;
  failureReason: string | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string | null;
  bankAccount?: {
    bankAccountId: string;
    accountName: string;
    bankCode?: string | null;
    bankName?: string | null;
    agency?: string | null;
    agencyDigit?: string | null;
    accountNumber?: string | null;
    accountDigit?: string | null;
    accountHolderName?: string | null;
    pixKey?: string | null;
    pixKeyType?: string | null;
  } | null;
  /** Employee (party) payee account pinned on payroll payments (ALC-3399). */
  partyBankAccount?: {
    bankAccountId: string;
    accountName: string;
    bankCode?: string | null;
    bankName?: string | null;
    agency?: string | null;
    agencyDigit?: string | null;
    accountNumber?: string | null;
    accountDigit?: string | null;
    accountHolderName?: string | null;
    pixKey?: string | null;
    pixKeyType?: string | null;
  } | null;
  originBankAccount?: {
    bankAccountId: string;
    accountName: string;
  } | null;
  creditCard?: {
    creditCardId: string;
    cardName: string;
  } | null;
  adjustments?: PaymentReconciliationAdjustmentResponse[];
  /**
   * When the payment was reconciled to a bank or card statement line, the calendar date (YYYY-MM-DD)
   * of that origin transaction (bank: effectiveAt ?? postedAt; card: postedAt).
   */
  reconciledOriginTransactionDate: string | null;
  /**
   * Id of the ACTIVE (CONFIRMED/RECONCILED) reconciliation group this payment belongs to, or null.
   * Resolved server-side from the live reconciliation link (or, as a fallback, from the
   * payment's `metadata.reconciliation.groupId` validated against an active group). It does
   * not depend on adjustments, so zero-adjustment reconciliations keep the link.
   */
  reconciliationGroupId: string | null;
  /** Additive warnings emitted while resolving payment defaults at creation time. */
  warnings?: PaymentCreationWarning[];
}

export interface CreatePaymentPayload {
  paymentNumber: number;
  paymentType: ApPaymentType;
  scheduledAmount?: number;
  scheduledDate: string;
  documentDueDate?: string;
  /**
   * On create: initial payment row status.
   * On update: transition to paid (`PAID`) or back to open (`SCHEDULED` when currently `PAID`).
   */
  initialStatus?: 'SCHEDULED' | 'PAID';
  paidAmount?: number;
  paidDate?: string;
  paymentMethod?: PaymentMethodType;
  bankAccountId?: string;
  /**
   * Employee (party) bank account for payroll-beneficiary bills (ALC-3399/3401).
   * Mutually exclusive with `bankAccountId`, which names a vendor account.
   */
  partyBankAccountId?: string;
  originBankAccountId?: string;
  creditCardId?: string;
  /** Number of installments when paying with a credit card (single AP payment for full amount). */
  ccInstallmentCount?: number;
  barcode?: string;
  boletoNossoNumero?: string;
  pixCopiaECola?: string;
  /**
   * DICT type of the Pix key sent in `pixCopiaECola`. Optional: when omitted the
   * server infers it and refuses with `PIX_KEY_TYPE_REQUIRED` if ambiguous.
   */
  pixKeyType?: PixKeyType;
  /**
   * PIX by the bank details of the registered payee account in `bankAccountId` /
   * `partyBankAccountId` (no key; `pixCopiaECola`/`pixKeyType` are sent null). Omitted or
   * false: PIX by key — `pixCopiaECola`, or the pointed account's key when it is omitted.
   */
  pixByBankData?: boolean;
  pixTxId?: string;
  pixEndToEndId?: string;
  tedDocNumber?: string;
  withholdingTaxType?: string;
  sourceDocumentId?: string;
  notes?: string;
  bankCode?: string;
  bankName?: string;
  agency?: string;
  agencyDigit?: string;
  accountNumber?: string;
  accountDigit?: string;
  accountType?: BankAccountType;
  accountHolderName?: string;
  /** When true, marks this payment as auto-generated during NF intake. Used by the boleto override flow. */
  isAutoGenerated?: boolean;
  /** When true, this payment is expected to be collected via automatic bank debit ("débito automático"). */
  isAutomaticBankPayment?: boolean;
  /**
   * Last 4 digits of the paying card from document extraction (not persisted).
   * Used at create time to resolve `creditCardId` and avoid defaulting to the wrong bank account.
   */
  documentPaymentCardLastFour?: string;
  /** Caller-generated key that makes a payment-create retry return the original payment. */
  idempotencyKey?: string;
}

/** API write shape for agent-created payments, whose sequence is allocated under the bill lock. */
export type AgentCreatePaymentPayload = Omit<CreatePaymentPayload, 'paymentNumber'> & {
  paymentNumber?: number;
};

/**
 * A payment PATCH. `pixCopiaECola: null` / `pixKeyType: null` clear the stored key — a
 * bank-details destination (TED, or `pixByBankData`) sends them null explicitly.
 */
export type UpdatePaymentPayload = Partial<
  Omit<CreatePaymentPayload, 'pixCopiaECola' | 'pixKeyType'>
> & {
  pixCopiaECola?: string | null;
  pixKeyType?: PixKeyType | null;
};

/** Two-step agent mutation lifecycle used by the AP payment write boundary. */
export const BillAgentPaymentUpdateAction = {
  PREVIEW: 'PREVIEW',
  EXECUTE: 'EXECUTE',
} as const;
export type BillAgentPaymentUpdateAction =
  (typeof BillAgentPaymentUpdateAction)[keyof typeof BillAgentPaymentUpdateAction];

/**
 * Public agent payment patch. Payment destinations are selected exclusively by
 * an eligible resolver method id; raw account ids and payment evidence never
 * cross this boundary.
 */
export interface BillAgentPaymentUpdatePatch {
  scheduledAmount?: number;
  scheduledDate?: string;
  documentDueDate?: string;
  paidAmount?: number;
  paidDate?: string;
  initialStatus?: 'SCHEDULED' | 'PAID';
  paymentMethodId?: string;
  notes?: string;
}

/** Canonical writer payload after the agent-only method id has been resolved. */
export interface BillAgentResolvedPaymentUpdatePatch extends Omit<
  BillAgentPaymentUpdatePatch,
  'paymentMethodId'
> {
  paymentMethod?: PaymentMethodType;
  bankAccountId?: string | null;
  partyBankAccountId?: string | null;
  sourceDocumentId?: string | null;
  barcode?: string | null;
  pixCopiaECola?: string | null;
  /**
   * Always false for an agent-chosen method: an account PIX method is the account's key and
   * TED/DOC/document methods carry no PIX by bank details. Agents cannot choose PIX by bank
   * details (no eligible method exposes it); a person does that in the payment dialog.
   */
  pixByBankData?: boolean;
}

/** Shared internal API request envelope for a confirmed agent payment correction. */
export interface BillAgentPaymentUpdateRequest {
  action: BillAgentPaymentUpdateAction;
  expectedResourceVersion?: string;
  patch: BillAgentPaymentUpdatePatch;
}

/** Task-shaped result shared by the API and MCP payment-update adapter. */
export interface BillAgentPaymentUpdateResponse {
  billId: string;
  paymentId: string;
  status: ApPaymentStatus;
  changedFields: string[];
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  warnings: string[];
  nextAction: string;
  resourceVersion?: string;
}

/** The bulk API allocates each payment's sequence under its bill lock. */
export type CreateBulkPaymentPayload = AgentCreatePaymentPayload;

export interface CreateBulkPaymentsRequest {
  payments: CreateBulkPaymentPayload[];
  totalAmount?: number;
}

export interface CreateBulkPaymentsResponse {
  payments: PaymentResponse[];
  count: number;
}

/**
 * The route that sends each payment through the provider its origin account is set up
 * with, instead of forcing one provider on the whole selection. It is how the payments page
 * sends, and how it asks what is ready before sending.
 */
export const AUTOMATIC_DISPATCH_ROUTE = 'automatic' as const;
export type PaymentDispatchRoute = PaymentDispatchProviderId | typeof AUTOMATIC_DISPATCH_ROUTE;

export interface AnalyzeSendSelectionRequest {
  paymentIds: string[];
  /** A provider to force, or `automatic` to follow each origin account's own setup. */
  integrationId: PaymentDispatchRoute;
}

export interface AnalyzeSendSelectionGroup {
  originBankAccountId: string | null;
  accountName: string | null;
  integrationId: string | null;
  paymentIds: string[];
}

export interface EligibleOriginAccount {
  id: string;
  name: string;
}

/**
 * Why a payment was refused before reaching the provider, as a stable code the web maps to
 * pt-BR copy. `reason` stays the developer-facing English sentence for logs and Swagger.
 *
 * Classes tell the UI who can act: `payee` is fixable from the payee/payment record by the
 * person sending; `config` is the origin account or provider setup, which the sender usually
 * cannot change and which is reported to Sentry; `system` is nothing the user did.
 */
export const PAYMENT_DISPATCH_REFUSAL_CODES = {
  // payee / payment record
  PAYEE_PIX_KEY_REQUIRED: 'payee',
  PAYEE_IDENTITY_REQUIRED: 'payee',
  PAYEE_BANK_DETAILS_REQUIRED: 'payee',
  /**
   * The payee's agência/conta are malformed for a COMPE bank (e.g. the agência carries its
   * check digit). Sending would credit another account or be rejected at the bank.
   */
  PAYEE_BANK_ACCOUNT_INVALID: 'payee',
  PAYEE_ACCOUNT_UNAVAILABLE: 'payee',
  BARCODE_REQUIRED: 'payee',
  BARCODE_INVALID: 'payee',
  AMOUNT_BARCODE_MISMATCH: 'payee',
  AMOUNT_INVALID: 'payee',
  SCHEDULED_DATE_REQUIRED: 'payee',
  DARF_FIELD_REQUIRED: 'payee',
  DARF_AMOUNT_INVALID: 'payee',
  DARF_MULTIPLE_REVENUE_CODES: 'payee',
  PAYMENT_METHOD_UNSUPPORTED: 'payee',
  /**
   * The payment's currency (`detail`) is not one the origin account's provider sends in: a
   * USD provider takes USD payments only, and the Brazilian providers take BRL only.
   */
  PAYMENT_CURRENCY_UNSUPPORTED: 'payee',
  /**
   * The payee's bank account is in a country (`detail`, ISO 3166-1 alpha-2) the origin
   * account's provider does not pay to: ACH reaches US accounts only.
   */
  PAYEE_ACCOUNT_COUNTRY_UNSUPPORTED: 'payee',
  PIX_QR_UNSUPPORTED: 'payee',
  /**
   * The payment has no reference number (`paymentReference`, a legacy row). A provider that
   * identifies the payment by it (the bank-file hub, ADR 0030 §3) and matches its returns on
   * it cannot send the payment: the bank's answer could never be attributed to it.
   */
  PAYMENT_REFERENCE_MISSING: 'payee',
  /**
   * The payment's reference number holds a character, or a length, the provider refuses as its
   * payment identifier (Finnet takes the reference as is and refuses a hyphen, ADR 0030 §3). The
   * reference is never rewritten, since the bank's return is matched on it as stored; the
   * payment is cancelled and created again to get a new one. Nothing was sent.
   */
  PAYMENT_REFERENCE_INVALID_FOR_PROVIDER: 'payee',
  // origin account / provider configuration
  ORIGIN_ACCOUNT_REQUIRED: 'config',
  ORIGIN_ACCOUNT_BANK_MISMATCH: 'config',
  ORIGIN_ACCOUNT_INCOMPLETE: 'config',
  /** The origin account exists but payment sending is not enabled and ready on it. */
  ORIGIN_ACCOUNT_NOT_READY: 'config',
  /** The origin account belongs to a different legal entity than the bill. */
  ORIGIN_ACCOUNT_ENTITY_MISMATCH: 'config',
  /**
   * The origin account is set to send through the bank-file hub but no hub account is mapped
   * to it (or the mapping is incomplete). Nothing leaves until operations maps the account.
   */
  FINNET_ACCOUNT_NOT_MAPPED: 'config',
  CAPABILITY_ROUTE_MISMATCH: 'config',
  CAPABILITY_NOT_CONFIGURED: 'config',
  CAPABILITY_BLOCKED: 'config',
  AMOUNT_EXCEEDS_LIMIT: 'config',
  TAX_SEGMENT_NOT_ENABLED: 'config',
  /**
   * The origin account's provider authorises per company (BTG, ADR 0032) and the entity's
   * connection is not usable: it lapsed (`detail` `LAPSED`), was disconnected (`REVOKED`) or
   * never existed (`NOT_CONNECTED`). A tenant admin reconnects it in Integrations.
   */
  PROVIDER_CONNECTION_LAPSED: 'config',
  /**
   * TecnoSpeed does not offer this payment form from the origin account's bank (its
   * "Bancos homologados" tables), for example PIX by key from Banco do Brasil or DARF from
   * Santander. `detail` is the form number.
   */
  ORIGIN_BANK_FORM_UNSUPPORTED: 'config',
  // nothing the user did
  PAYMENT_NOT_FOUND: 'system',
  /** A previous send already has this payment in flight; follow that one. */
  ALREADY_IN_FLIGHT: 'system',
  /**
   * An active bank-payment file already holds this payment. The file is the instruction the
   * bank will act on, so sending the same payment through a provider would pay it twice.
   */
  ALREADY_IN_BANK_FILE: 'system',
  /**
   * The payment's origin account (or its bill's payer) changed between the moment the send
   * was prepared and the moment its manifest was written. The manifest names the account the
   * money leaves from, so the payment is left out of this send; re-send it from its new origin.
   */
  ORIGIN_ACCOUNT_CHANGED: 'system',
  /**
   * The boleto's bar code carries a due date (its due-date factor) that differs from the
   * bill's: the bill and the code describe two different debts, so the payment is not sent.
   */
  BARCODE_DUE_DATE_MISMATCH: 'payee',
  /**
   * A guide paid by its fields (no code) lacks one the provider needs, or holds one it cannot
   * carry (a GPS identified by NIT or CEI): `detail` names the field.
   */
  TAX_FIELD_REQUIRED: 'payee',
  /** The guide's amounts (principal, other entities, fine, interest) do not add up to the payment. */
  TAX_AMOUNT_INVALID: 'payee',
  /**
   * The date the payment would go out on (`detail`, `YYYY-MM-DD`) is a Saturday or Sunday and
   * the provider pays on business days only. The day the money leaves is the operator's call,
   * so it is not moved: the payment is rescheduled and sent again.
   */
  PAYMENT_DATE_NOT_BUSINESS_DAY: 'payee',
  /**
   * The payment would go out after the document's due date (`detail`, `<payment> > <due>`): a
   * boleto or a DARF paid by its fields carries its face value only, and the provider refuses
   * to schedule one past its due date. A due date on a weekend counts until the Monday after.
   */
  PAYMENT_DATE_AFTER_DUE_DATE: 'payee',
} as const satisfies Record<string, PaymentDispatchRefusalClass>;

/**
 * Codes of the Finnet connection admin routes (`integrations/finnet`): why listing the hub's
 * accounts or mapping a bank account to one was refused. They travel as `{ code, message }` in
 * the error body; the admin screen words them from the code.
 */
export const FINNET_CONNECTION_ERROR_CODES = {
  /** The platform's Finnet login is not set in this environment. */
  FINNET_NOT_CONFIGURED: 409,
  /** The tenant is not enabled for Finnet. */
  FINNET_NOT_ENABLED: 409,
  /** The login's first-access password was never changed. */
  FINNET_PASSWORD_CHANGE_REQUIRED: 409,
  /** Finnet could not be reached or answered with an error. */
  FINNET_UNAVAILABLE: 502,
  /** No Finnet configuration with that id belongs to this tenant's companies. */
  FINNET_CONFIG_NOT_FOUND: 404,
  /** The configuration is at another bank than the bank account. */
  FINNET_CONFIG_BANK_MISMATCH: 422,
  /** The configuration lacks what a send needs (numeric branch and account). */
  FINNET_CONFIG_UNUSABLE: 422,
  /** The Finnet configuration is already mapped to a bank account. */
  FINNET_ACO_ALREADY_MAPPED: 409,
  /** The bank account is already mapped; remove that mapping first. */
  FINNET_BANK_ACCOUNT_ALREADY_MAPPED: 409,
  /** The bank account has no Finnet mapping to remove. */
  FINNET_MAPPING_NOT_FOUND: 404,
  /**
   * The public URL Finnet must call back (`PUBLIC_API_BASE_URL`) is not set in this
   * environment, so the account's webhook cannot be registered.
   */
  FINNET_WEBHOOK_URL_NOT_CONFIGURED: 409,
} as const satisfies Record<string, number>;

export type FinnetConnectionErrorCode = keyof typeof FINNET_CONNECTION_ERROR_CODES;

/**
 * Refusals of "Cancelar envio" (`POST ap/bills/payments/:paymentId/cancel-send`), by the HTTP
 * status they travel with as `{ code, message, providerStatus?, paymentStatus?, vendorCode? }`.
 * Whatever the code, nothing was rolled back locally: a payment returns to a sendable status
 * only after the provider's documented success answer (ADR 0032 "Cancel"). The web words each
 * code in pt-BR and en; operator copy never names the vendor.
 */
export const PAYMENT_SEND_CANCEL_REFUSAL_CODES = {
  /** The payment is not in processing (`paymentStatus`); there is no send to withdraw. */
  PAYMENT_NOT_PROCESSING: 409,
  /** The payment has no provider send that can still be cancelled. */
  NO_ACTIVE_PROVIDER_ATTEMPT: 409,
  /** The provider reports a status (`providerStatus`) from which it offers no cancellation. */
  SEND_NOT_CANCELLABLE: 409,
  /** A cancellation was already requested from the bank; its confirmation is awaited. */
  CANCEL_ALREADY_REQUESTED: 409,
  /** The payment changed while its send was being cancelled. */
  PAYMENT_CHANGED_DURING_CANCEL: 409,
  /** A linked remittance may already be at the bank; the operator must check there first. */
  FILE_MAY_BE_AT_BANK: 409,
  /** The provider could not be asked (a read failed); nothing changed, try again. */
  PROVIDER_CALL_FAILED: 502,
  /**
   * The provider accepted the send and offers no cancellation of its own (the bank-file hub,
   * ADR 0030): the operator cancels at the bank, and the bank's return settles the payment.
   */
  SEND_CANCEL_AT_BANK_ONLY: 409,
  /**
   * The bank already confirmed or processed the payment (`providerStatus`): only the bank can
   * reverse it, so the operator contacts the bank.
   */
  SEND_PAST_CANCELLATION: 409,
  /**
   * The provider does not list the payment yet (its own payment id is unknown, or its batch is
   * no longer open): the next results poll learns it, then the cancel can be asked again.
   */
  SEND_NOT_YET_LISTED: 409,
  /**
   * The provider was asked to cancel and did not answer its documented success (a refusal
   * code in `vendorCode`, a 5xx, a timeout). Nothing changed locally; the cancel intent stays
   * on record and the next results poll settles what the provider holds.
   */
  SEND_CANCEL_NOT_CONFIRMED: 502,
  /** A send or a token renewal holds the provider connection right now; try again shortly. */
  SEND_CANCEL_BUSY: 409,
  /** This environment has no client for the provider that made the send. */
  SEND_CANCEL_UNAVAILABLE: 503,
  /**
   * The company's connection to the provider lapsed (or the provider refused its token while
   * cancelling, which lapses it): a tenant admin reconnects it, then the cancel is asked again.
   */
  PROVIDER_CONNECTION_LAPSED: 409,
} as const satisfies Record<string, number>;

export type PaymentSendCancelRefusalCode = keyof typeof PAYMENT_SEND_CANCEL_REFUSAL_CODES;

export type PaymentDispatchRefusalClass = 'payee' | 'config' | 'system';
export type PaymentDispatchRefusalCode = keyof typeof PAYMENT_DISPATCH_REFUSAL_CODES;

export function paymentDispatchRefusalClass(
  code: PaymentDispatchRefusalCode | string | undefined,
): PaymentDispatchRefusalClass {
  return (
    (code &&
      (PAYMENT_DISPATCH_REFUSAL_CODES as Record<string, PaymentDispatchRefusalClass>)[code]) ||
    'system'
  );
}

/**
 * The origin account's payment provider (`paymentSetupProvider`) cannot be switched while
 * money from the account may still move (ADR 0030 §2): an unsettled dispatch manifest that
 * names it, or holds one of its payments, and still has a payment in flight or none named,
 * or an unresolved provider attempt for a payment from it that is not already paid.
 * A manifest whose payments are already terminal does not block. A switch then would leave
 * money moving through a provider the account no longer names. Returned by
 * `PATCH op/bank-accounts/:id/payment-provider` as a 409 whose `details` are
 * {@link ProviderSwitchBlockedDetails}; the web maps it to pt-BR and en copy.
 */
export const PROVIDER_SWITCH_BLOCKED = 'PROVIDER_SWITCH_BLOCKED';

/** What holds a provider switch: the open sends and the unresolved attempts from the account. */
export interface ProviderSwitchBlockedDetails {
  /** `PaymentDispatchBatch.batchId` of unsettled sends that still name money in flight. */
  openSendIds: string[];
  /** `PaymentProviderAttempt.attemptId` of unresolved attempts whose payment is not paid. */
  unresolvedAttemptIds: string[];
}

/** Body of a payment-provider switch on an origin bank account (an internal admin setting). */
export interface SwitchBankAccountPaymentProviderRequest {
  /** A provider id the provider registry holds. */
  providerId: PaymentDispatchProviderId;
}

/**
 * Why a payment awaits an operator's decision (ADR 0031 §4-6). Needs-review is a marker on the
 * payment, not a status: a provider outcome Alchemy ERP must neither settle nor fail on its
 * own puts it there, and an operator's resolution (or, for the reasons marked `true`, a later
 * vendor result that settles the payment) takes it off.
 */
export const PAYMENT_NEEDS_REVIEW_REASONS = {
  /**
   * The bank reversed a payment already marked paid. The payment is REVERSED, but its payment
   * journal and the bill's paid state were written at settlement and are not undone here.
   */
  REVERSED_AFTER_PAYMENT: false,
  /** The bank reversed a payment after reconciliation; reconciliation owns its status. */
  REVERSED_AFTER_RECONCILIATION: false,
  /** The provider accepted the payment and gave no result by the attempt's return-due time. */
  NO_PROVIDER_RESULT_BY_DUE_TIME: true,
  /**
   * The provider reported a code the outcome table does not hold, or one whose effect on a
   * payment Alchemy ERP sent is not certain (a duplicate, an instruction we never send). It
   * neither settles nor fails the payment; a later result that settles it takes it off.
   */
  PROVIDER_RESULT_CODE_UNKNOWN: true,
  /**
   * The bank reported the payment made, but the amount paid (or the effective date) is missing
   * or differs from what was sent. The payment is not settled.
   */
  PROVIDER_RESULT_AMOUNT_MISMATCH: false,
  /**
   * A result carries this payment's reference but does not match the send on record: another
   * origin account, a send whose acceptance was never confirmed, or a payment already closed
   * another way. Nothing was applied.
   */
  PROVIDER_RESULT_UNMATCHED: false,
  /**
   * The bank holds the payment in its fraud checks, or reported it invalidated: not paid, not
   * failed, and nothing Alchemy ERP can do on its own. An operator checks it at the bank; a
   * later result that settles the payment takes it off.
   */
  PROVIDER_PAYMENT_HELD: true,
} as const satisfies Record<string, boolean>;

export type PaymentNeedsReviewReason = keyof typeof PAYMENT_NEEDS_REVIEW_REASONS;

/** Whether a later vendor result that settles the payment also takes it off needs-review. */
export function isNeedsReviewClearedByVendorResult(reason: string | null | undefined): boolean {
  return Boolean(
    reason && (PAYMENT_NEEDS_REVIEW_REASONS as Record<string, boolean>)[reason] === true,
  );
}

/**
 * 409 of `POST ap/payments/:paymentId/needs-review/resolve` when the payment awaits no review:
 * it was never raised, or someone (or a vendor result) resolved it first.
 */
export const PAYMENT_NOT_IN_NEEDS_REVIEW = 'PAYMENT_NOT_IN_NEEDS_REVIEW';

/** One payment awaiting an operator's decision, as `GET ap/payments/needs-review` lists it. */
export interface PaymentNeedsReviewItem {
  paymentId: string;
  billId: string;
  reason: PaymentNeedsReviewReason | string;
  /** ISO-8601; when the payment was put in needs-review. */
  raisedAt: string;
  /** What the vendor (or the sweep) observed; for support, may be shown verbatim. */
  evidence: Record<string, unknown> | null;
  status: string;
  scheduledAmount: number | null;
  currencyCode: string;
  scheduledDate: string | null;
  externalPaymentId: string | null;
  paymentReference: string | null;
  vendorName: string | null;
  vendorBillNumber: string | null;
  originBankAccount: { bankAccountId: string; name: string | null } | null;
}

export interface PaymentNeedsReviewListResponse {
  items: PaymentNeedsReviewItem[];
  total: number;
}

export interface ResolvePaymentNeedsReviewRequest {
  /** What the operator checked and decided; recorded on the payment history. */
  note: string;
}

export interface ResolvePaymentNeedsReviewResponse {
  paymentId: string;
  resolvedReason: PaymentNeedsReviewReason | string;
  /** ISO-8601. */
  resolvedAt: string;
}

export interface IneligiblePaymentReason {
  paymentId: string;
  /** Developer-facing sentence. Never shown to the user as-is. */
  reason: string;
  code?: PaymentDispatchRefusalCode;
  class?: PaymentDispatchRefusalClass;
  /** Free-form detail the copy may interpolate (a field name, a segment, an amount pair). */
  detail?: string;
}

/** Builds a refusal row with its class derived from the code. */
export function paymentDispatchRefusal(
  paymentId: string,
  code: PaymentDispatchRefusalCode,
  reason: string,
  detail?: string,
): IneligiblePaymentReason {
  return {
    paymentId,
    reason,
    code,
    class: paymentDispatchRefusalClass(code),
    ...(detail !== undefined && { detail }),
  };
}

/**
 * Why a send the server tried to conclude is stuck rather than merely pending.
 *
 * A send whose payments never all reach a terminal state cannot settle by waiting longer, and
 * that is not the same thing as a send still in progress: Finance has to be able to tell them
 * apart, and the state is recorded once with the code so it is a durable fact and not an alert
 * that fires again every minute.
 */
export const PAYMENT_DISPATCH_STALL_CODES = {
  /** Payments of this send never all reached a terminal state at the provider. */
  PAYMENTS_NEVER_TERMINAL: 'system',
  /** The provider's status call kept failing, so the send could not be concluded. */
  PROVIDER_STATUS_UNAVAILABLE: 'system',
  /**
   * The send itself is no longer settleable: its payments left the scope it was dispatched
   * through, no send was recorded under the correlation id, or the set no longer matches.
   * Unlike an outage, waiting does not help -- someone has to decide what the send is now.
   */
  MANIFEST_REJECTED: 'config',
  /** The send's payments cannot be attributed to one payer and origin account. */
  SCOPE_UNRESOLVED: 'config',
} as const satisfies Record<string, PaymentDispatchRefusalClass>;

export type PaymentDispatchStallCode = keyof typeof PAYMENT_DISPATCH_STALL_CODES;

/**
 * Scalar evidence stored with a stall, so the record answers "what do I do about it" without
 * anyone opening the logs. Every field is optional: what is known depends on how far the
 * settlement got before it stopped.
 */
export interface PaymentDispatchStallEvidence {
  correlationId: string;
  providerId: string;
  paymentIds: string[];
  /** The payments that had not reached a terminal state, with the state each was in. */
  nonTerminal?: { paymentId: string; state: string }[];
  /** The refusal code the API answered with, when the settlement was refused rather than lost. */
  errorCode?: string;
  httpStatus?: number;
  /**
   * The payments the refusal named, each with its own cause. A refusal that says three
   * payments left the dispatched scope is only actionable if the row says which three.
   */
  refusedPayments?: { paymentId: string; code?: string }[];
  /**
   * What the send was recorded against, and where the named payments sit now. Both halves,
   * because either alone leaves the reader deducing the other.
   */
  recordedScope?: { entityId: string | null; bankAccountId: string | null };
  observedScopes?: { paymentId: string; entityId: string | null; bankAccountId: string | null }[];
  /** Payer/origin pairs the send's attempts resolved to, when any did. */
  scopes?: { entityId: string; bankAccountId: string }[];
  /** How many payments of the send resolved to no payer/origin at all. */
  unresolvedScopeCount?: number;
  /**
   * Payments of the send whose job was still queued and that name no origin account yet.
   * They do not contradict the scope the send's attempts resolved to -- the same dispatch
   * command enqueued them -- so they are reported rather than treated as unresolved.
   */
  queuedScopeUnknownCount?: number;
}

/**
 * Why a payment cannot go into an institution-specific bank payment file (the XLSX and
 * similar layouts a bank accepts by hand, not CNAB and not a provider send).
 *
 * Same contract as the dispatch refusals above and for the same reason: the server names the
 * cause with a stable code and keeps its English sentence for developers, and the pt-BR the
 * operator reads is written once on the web. A code here is part of the API.
 */
export const BANK_FILE_EXPORT_BLOCK_CODES = {
  // the payment or its bill
  BILL_NOT_STATUTORY: 'payee',
  DOCUMENT_NOT_STATUTORY: 'payee',
  PAYMENT_NOT_EXPORTABLE: 'payee',
  PIX_NOT_SUPPORTED: 'payee',
  INVALID_BARCODE: 'payee',
  PAYMENT_METHOD_BARCODE_MISMATCH: 'payee',
  BARCODE_AMOUNT_MISMATCH: 'payee',
  UNTRUSTED_TAX_ID: 'payee',
  /** The payee's tax id is trustworthy, so the ordinary rails take this payment. */
  TRUSTED_TAX_ID_USE_CNAB: 'payee',
  // the origin account or the selection
  MISSING_ORIGIN_ACCOUNT: 'config',
  UNSUPPORTED_BANK_ADAPTER: 'config',
  CURRENCY_MISMATCH: 'config',
  MIXED_ORIGIN_BANK_ACCOUNTS: 'config',
  ORIGIN_ENTITY_MISMATCH: 'config',
  // already spoken for, or gone
  PAYMENT_ALREADY_EXPORTED: 'system',
  PAYMENT_ALREADY_SENT: 'system',
  PAYMENT_NOT_FOUND: 'system',
} as const satisfies Record<string, PaymentDispatchRefusalClass>;

export type BankFileExportBlockCode = keyof typeof BANK_FILE_EXPORT_BLOCK_CODES;

export function bankFileExportBlockClass(
  code: BankFileExportBlockCode | string | undefined,
): PaymentDispatchRefusalClass {
  return (
    (code && (BANK_FILE_EXPORT_BLOCK_CODES as Record<string, PaymentDispatchRefusalClass>)[code]) ||
    'system'
  );
}

export interface BankFileExportBlockReason {
  code: BankFileExportBlockCode;
  /** Developer-facing sentence. Never shown to the user as-is. */
  reason: string;
  /**
   * Free-form detail the pt-BR copy may interpolate: a payment status, a digit count, or the
   * `encoded/scheduled` amount pair (both as plain decimals, so the web formats the currency).
   */
  detail?: string;
}

/** Builds a bank-file block reason with its class derived from the code. */
export function bankFileExportBlock(
  code: BankFileExportBlockCode,
  reason: string,
  detail?: string,
): BankFileExportBlockReason {
  return { code, reason, ...(detail !== undefined && { detail }) };
}

/** Body of the 400 thrown when a send has nothing left to enqueue. */
export const PAYMENT_DISPATCH_NO_ELIGIBLE_PAYMENTS = 'PAYMENT_DISPATCH_NO_ELIGIBLE_PAYMENTS';

/**
 * Body of the 503 thrown when the queue failed part-way through a send. The payments in
 * `pending` are in flight under `correlationId` and settle through `dispatch-status` like
 * any send; they must be followed up there, never resent. The rest were never enqueued.
 */
export const PAYMENT_DISPATCH_PARTIAL_ENQUEUE = 'PAYMENT_DISPATCH_PARTIAL_ENQUEUE';

/**
 * One provider send that did go out, as handed back when a later one in the same request
 * failed. Every enqueued group is listed here whether or not the provider left `pending` ids
 * to follow up: a provider that finishes synchronously (its worker updates the rows on its
 * own) still put these payments in flight, and a retry must not send them again.
 */
export interface PaymentDispatchHandoff {
  provider: PaymentDispatchProviderId;
  /** The send's own id; the manifest and its status are read under it. */
  correlationId: string;
  paymentIds: string[];
}

export interface PaymentDispatchPartialEnqueueError {
  code: typeof PAYMENT_DISPATCH_PARTIAL_ENQUEUE;
  message: string;
  correlationId: string;
  provider: PaymentDispatchProviderId;
  /** Every payment this send may have put in flight; the set to follow up under `correlationId`. */
  pending: PaymentDispatchPending[];
  /**
   * Every group that was enqueued before the failure, one entry per (provider, origin) send.
   * A superset of `pending`: it also names the sends whose provider returned nothing to poll.
   * The payments here are not to be resent; `failedPaymentIds` are.
   */
  dispatched?: PaymentDispatchHandoff[];
  /**
   * Payments in `pending` whose enqueue outcome could not be confirmed either way (the queue
   * call died and the queue could not be asked afterwards). They stay in the send so a job
   * that did land is never orphaned; a job that never landed reads as QUEUED until re-sent.
   */
  uncertainPaymentIds?: string[];
  /** Payments refused before dispatch by the groups that did run. */
  skipped?: IneligiblePaymentReason[];
  /**
   * The payments of the group whose send failed outright, so the operator knows exactly what
   * to resend. Neither in flight nor refused: never enqueued.
   */
  failedPaymentIds?: string[];
  cause?: string;
}

export interface AnalyzeSendSelectionResponse {
  groups: AnalyzeSendSelectionGroup[];
  eligibleOriginAccountIds: string[];
  eligibleOriginAccounts: EligibleOriginAccount[];
  allOriginsValidForIntegration: boolean;
  ineligiblePayments: IneligiblePaymentReason[];
  eligiblePaymentIds: string[];
}

export interface SendPaymentsToProviderRequest {
  paymentIds: string[];
  forceProvider?: PaymentDispatchProviderId;
  /**
   * Send through the origin account's armed VAN. Allowed only for payments the API
   * refused before accepting. Ignored when `forceProvider` is set.
   */
  viaArmedVan?: boolean;
}

export interface PaymentDispatchRemittanceFile {
  fileName: string;
  contentBase64: string;
  /**
   * The payments this file actually carries: the accepted subset of the send. Smaller than
   * the polled set whenever the provider refused part of it, so the caller can state what
   * went to the bank instead of inferring it from the file.
   */
  coveredPaymentIds: string[];
}

/**
 * Payments a provider accepted for dispatch whose outcome is not known yet.
 *
 * A provider that registers each payment through a worker answers the send with this
 * instead of blocking the request until the worker is done: the caller follows up with
 * `POST payments/dispatch-status` until every payment settles. Providers that finish
 * synchronously leave it empty.
 */
export interface PaymentDispatchPending {
  provider: PaymentDispatchProviderId;
  paymentIds: string[];
  /**
   * The send this group belongs to, when it differs from the response's `correlationId`.
   * An automatic send from several origin accounts is several sends, one manifest each, and
   * each is followed up under its own id.
   */
  correlationId?: string;
}

export interface PaymentDispatchEnqueueResponse {
  jobId: string;
  correlationId: string;
  dispatchedPaymentCount?: number;
  skippedPaymentCount?: number;
  /**
   * Exactly the payments this send put in flight, by id. A provider reports identity, never
   * only counts: the caller builds every handoff (what to follow up, what not to resend) from
   * this list, and a count cannot say which payment it counts.
   */
  dispatchedPaymentIds?: string[];
  /** Payments refused before dispatch, each with its reason. */
  skipped?: IneligiblePaymentReason[];
  /** Payments handed to a worker and still in flight; poll `dispatch-status` for them. */
  pending?: PaymentDispatchPending[];
  remittance?: PaymentDispatchRemittanceFile;
  remittances?: PaymentDispatchRemittanceFile[];
}

/**
 * Where one payment stands in a dispatch the caller is following up on.
 *
 *   - `QUEUED`    — the worker has not picked it up yet: no attempt exists for it
 *   - `SENDING`   — the worker opened an attempt and is talking to the provider
 *   - `RETRYING`  — the provider could not be reached; the queue is retrying with backoff
 *   - `ACCEPTED`  — the provider registered it
 *   - `AWAITING_BANK_APPROVAL` — the provider registered it and the bank holds it until the
 *     customer's signatories approve it in the bank's own app; the default after acceptance on
 *     a capability that `requiresBankApproval` (ADR 0032), until a vendor result moves it
 *   - `REJECTED`  — the provider refused it; `errorCode` / `rejection` say why
 *   - `ABANDONED` — retries exhausted or the attempt was given up
 *
 * `ACCEPTED`, `AWAITING_BANK_APPROVAL`, `REJECTED` and `ABANDONED` are terminal for the send:
 * the approval happens at the bank, and the send never waits for it. `QUEUED` and `SENDING`
 * are kept apart because they answer different questions: a send that sits in `QUEUED` for
 * minutes means the worker is not running; one that sits in `SENDING` means the provider is
 * slow to answer.
 */
export type PaymentDispatchPaymentState =
  | 'QUEUED'
  | 'SENDING'
  | 'RETRYING'
  | 'ACCEPTED'
  | 'AWAITING_BANK_APPROVAL'
  | 'REJECTED'
  | 'ABANDONED';

/** Every per-payment dispatch state, for Swagger enums and exhaustive tables. */
export const PAYMENT_DISPATCH_PAYMENT_STATES = [
  'QUEUED',
  'SENDING',
  'RETRYING',
  'ACCEPTED',
  'AWAITING_BANK_APPROVAL',
  'REJECTED',
  'ABANDONED',
] as const satisfies readonly PaymentDispatchPaymentState[];

export const PAYMENT_DISPATCH_TERMINAL_STATES: ReadonlySet<PaymentDispatchPaymentState> =
  new Set<PaymentDispatchPaymentState>([
    'ACCEPTED',
    'AWAITING_BANK_APPROVAL',
    'REJECTED',
    'ABANDONED',
  ]);

/** The send reached a verdict for this payment; nothing further will change for the send. */
export function isPaymentDispatchTerminalState(state: string | null | undefined): boolean {
  return PAYMENT_DISPATCH_TERMINAL_STATES.has(state as PaymentDispatchPaymentState);
}

/**
 * The provider registered the payment: plain acceptance, or acceptance the bank still holds
 * for its signatories' approval. Both go into what a send settles as accepted.
 */
export function isPaymentDispatchAcceptedState(
  state: string | null | undefined,
): state is 'ACCEPTED' | 'AWAITING_BANK_APPROVAL' {
  return state === 'ACCEPTED' || state === 'AWAITING_BANK_APPROVAL';
}

/**
 * `rejection.code` of a payment the bank failed because its signatories did not approve it in
 * time (a bank result that names the approval deadline, ADR 0032). Operator copy names it as
 * "não aprovado a tempo", never with the vendor's own code.
 */
export const PROVIDER_APPROVAL_EXPIRED = 'PROVIDER_APPROVAL_EXPIRED';

export interface PaymentDispatchPaymentStatus {
  paymentId: string;
  state: PaymentDispatchPaymentState;
  externalPaymentId?: string | null;
  errorCode?: string | null;
  /**
   * The bank's refusal, mapped through the FEBRABAN/TecnoSpeed catalogue. The raw provider
   * text stays on the attempt row for support; it is never returned to the browser.
   */
  rejection?: { code: string; severity: 'retryable' | 'permanent' | 'unknown' } | null;
}

export interface PaymentDispatchStatusRequest {
  correlationId: string;
  provider: PaymentDispatchProviderId;
  paymentIds: string[];
}

export interface PaymentDispatchStatusResponse {
  correlationId: string;
  provider: PaymentDispatchProviderId;
  payments: PaymentDispatchPaymentStatus[];
  /** Every payment is in a terminal state; nothing further will change. */
  settled: boolean;
  /**
   * Present once the send settled with at least one accepted payment. A send where some
   * payments were refused still produces the remittance of the accepted ones: a refusal is
   * terminal, so waiting for it to turn into an acceptance would strand the rest for ever.
   * `coveredPaymentIds` is exactly what the file contains.
   *
   * The first of `remittances`, kept for readers from before a send could produce more than
   * one file.
   */
  remittance?: PaymentDispatchRemittanceFile;
  /**
   * Every remittance file the send produced: one per family of its accepted payments, since a
   * PIX never shares a remittance with another method. A send of boletos and PIX has two, and
   * each must reach the bank.
   */
  remittances?: PaymentDispatchRemittanceFile[];
}

/**
 * A provider send the tenant still has to hear the end of: unsettled, or found stuck
 * recently enough that someone should still see it.
 *
 * Read from the dispatch manifest (`PaymentDispatchBatch`), which the server keeps
 * regardless of any browser: the page rebuilds its progress banner and per-payment chips
 * from this after a reload, on another machine, or the next morning. The per-payment
 * states are not here -- the page polls `dispatch-status` for them as it does after
 * an enqueue -- so this answers "what is open", not "how far is each one".
 */
export interface OpenPaymentDispatch {
  batchId: string;
  correlationId: string;
  provider: PaymentDispatchProviderId;
  /** Exactly the payments the send put in flight. */
  paymentIds: string[];
  /** ISO-8601; when the send was enqueued. */
  createdAt: string;
  requestedBy: { userId: string; name: string } | null;
  /** The origin account the send was recorded against, when its scope was known. */
  originBankAccount: { bankAccountId: string; name: string } | null;
  /**
   * Present when the server found the send stuck. A stalled send is still unsettled, but
   * waiting no longer helps: the code says why and `nonTerminalPaymentIds` says which
   * payments never got an answer, when that is what stalled it.
   */
  stall: {
    code: PaymentDispatchStallCode | string;
    stalledAt: string;
    /** What was observed, for support; may be shown verbatim in a tooltip. */
    detail: string | null;
    nonTerminalPaymentIds: string[];
  } | null;
}

export interface OpenPaymentDispatchesResponse {
  dispatches: OpenPaymentDispatch[];
}

/**
 * A stalled send concluded by a person: its manifest is settled with what is known at that
 * moment, so the payments it held are released (their origin may change again) and the send
 * leaves the open list. Recorded on the manifest with who concluded it and when.
 */
/**
 * What the bank told the operator about a payment the send never got an answer for.
 * `NOT_EXECUTED` releases it to be sent again. A payment the bank did execute is not a
 * disposition here: it is settled through the ledger (bank reconciliation or manual
 * settlement), and a payment already paid there reads as accepted when the send concludes.
 */
export type PaymentDispatchDispositionOutcome = 'NOT_EXECUTED';

export interface PaymentDispatchDisposition {
  paymentId: string;
  outcome: PaymentDispatchDispositionOutcome;
}

/**
 * Concluding a stalled send needs a disposition for every payment the provider gave no
 * terminal answer for. Payments the status already reports as accepted or refused need none;
 * naming them anyway is ignored, the provider's answer wins.
 */
export interface ConcludePaymentDispatchRequest {
  dispositions?: PaymentDispatchDisposition[];
}

/** Refusal when a non-terminal payment was left without a disposition. */
export const PAYMENT_DISPATCH_DISPOSITION_REQUIRED = 'PAYMENT_DISPATCH_DISPOSITION_REQUIRED';
/**
 * Refusal when the send cannot be concluded by hand yet: the sweep has not found it stuck,
 * or a worker of the send holds the provider lease (it is talking to the gateway right now;
 * `leaseUntil` says until when).
 */
export const PAYMENT_DISPATCH_STILL_MOVING = 'PAYMENT_DISPATCH_STILL_MOVING';
/** Refusal when the send was concluded already, by a person, the sweep or a concurrent call. */
export const PAYMENT_DISPATCH_ALREADY_SETTLED = 'PAYMENT_DISPATCH_ALREADY_SETTLED';
/**
 * Refusal when a payment worker still running the previous code is connected to the send's
 * queue: it would consume a job without claiming the send, so a manual conclusion cannot
 * release the payments yet. Drain the old workers (finish the deploy) and try again.
 */
export const PAYMENT_DISPATCH_WORKERS_NOT_LEASE_AWARE = 'PAYMENT_DISPATCH_WORKERS_NOT_LEASE_AWARE';
/** Refusal when the queue could not say which workers are connected; the conclusion fails closed. */
export const PAYMENT_DISPATCH_WORKERS_UNKNOWN = 'PAYMENT_DISPATCH_WORKERS_UNKNOWN';
/** Refusal when another conclusion holds the provider's queue paused right now; try again shortly. */
export const PAYMENT_DISPATCH_QUEUE_BUSY = 'PAYMENT_DISPATCH_QUEUE_BUSY';
/** Refusal when the provider's queue could not be paused for the conclusion; it fails closed. */
export const PAYMENT_DISPATCH_QUEUE_UNAVAILABLE = 'PAYMENT_DISPATCH_QUEUE_UNAVAILABLE';

export interface ConcludePaymentDispatchResponse {
  batchId: string;
  correlationId: string;
  provider: PaymentDispatchProviderId;
  settledAt: string;
  acceptedPaymentIds: string[];
  rejectedPaymentIds: string[];
}

/**
 * How long a stalled send stays in the open list. After this the stall is history that the
 * settlement record and Sentry hold; it stops greeting whoever opens the payments page.
 */
export const OPEN_PAYMENT_DISPATCH_STALL_WINDOW_HOURS = 72;

/**
 * Backward-compatible alias kept while app code migrates naming.
 */
export type KaminoPaymentDispatchEnqueueResponse = PaymentDispatchEnqueueResponse;
